/**
 * useCompactDdlEditContext — the former inline-toolbar-host wiring, hosted by
 * the Compact VQE popover instead of the (now removed) always-on inline DDL
 * toolbar.
 *
 * It is the SAME three-step pipeline the host ran, minus the toolbar render:
 *   1. `useDdlStatementDetection` parses the buffer with `compoundBlockAware`
 *      options and classifies the statement under the cursor. Its parse — NOT
 *      the DML `cursorEditInfo` — owns the `statementId`, so the detection
 *      hook and the edit-context service agree on statement boundaries (a
 *      CREATE PROCEDURE body with inner `;` stays ONE statement).
 *   2. `useInlineDdlEditContext` binds that statement to a per-tab
 *      `SqlResolverService` over the Monaco buffer (`VqeDdlEditContext`).
 *   3. `useInlineDdlBlastRadiusGate` splices the locked `'inline'`-sourced
 *      blast-radius guard on, so destructive drops still pause with the
 *      BlastRadiusPanel (PRD VQE-D7 "always on" / VQE-D9).
 *
 * `enabled: false` makes the whole pipeline inert: detection short-circuits
 * (no cursor polling, no subscriptions), the edit context is parked on an
 * empty buffer with no connection (no schema-snapshot IPC, no live pull), and
 * every returned field is `null`. Hosts additionally gate the MOUNT —
 * `CompactDdlEditor` only calls this hook while the popover is open — so a
 * closed popover allocates no resolver service at all.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice A1 (Variant 1) + Slice H3.
 * Redesign: .superpowers/sdd/2026-07-28-compact-visual-ddl-editor/ (T3).
 */
import { useMemo } from 'react';

import type { ParseOptions } from '../../../sql-grammar/model';
import type { SqlMonacoHandle } from '../../common/utils/sqlMonacoTypes';
import type { DdlObjectKind, VqeDdlEditContext } from '../edit/editContext';
import { useDdlStatementDetection } from '../inline/useDdlStatementDetection';
import { useInlineDdlBlastRadiusGate } from '../inline/useInlineDdlBlastRadiusGate';
import { useInlineDdlEditContext } from '../inline/useInlineDdlEditContext';

export type CompactDdlEditState = {
  /** Wired DDL edit context for the parked statement; `null` → fallback card. */
  readonly editContext: VqeDdlEditContext | null;
  /** Classified object kind — drives the compact body-arm dispatch. */
  readonly objectKind: DdlObjectKind | null;
  /** Classified DDL kind (`'grant'` | `'create-view'` | …); arms that host two
   *  forms for one object kind (e.g. security) branch on it. */
  readonly ddlKind: string | null;
};

export type UseCompactDdlEditContextArgs = {
  readonly editorRef: React.RefObject<SqlMonacoHandle | null>;
  readonly tabId: string;
  readonly query: string;
  readonly options: ParseOptions;
  /** `open && editTarget === 'ddl-card'` at the host. */
  readonly enabled: boolean;
  readonly connectionId?: string | null;
  readonly database?: string | null;
};

const EMPTY_QUERY = '';

export function useCompactDdlEditContext(
  args: UseCompactDdlEditContextArgs,
): CompactDdlEditState {
  const { editorRef, tabId, query, options: baseOptions, enabled, connectionId, database } = args;

  // Compound BEGIN…END awareness — opt-in only, exactly as the inline host
  // set it: routine/trigger bodies with inner `;` must resolve to a single
  // editable statement in BOTH the detection parse and the edit-context
  // service, otherwise their `statementId`s drift apart.
  const options = useMemo<ParseOptions>(
    () => ({ ...baseOptions, compoundBlockAware: true }),
    [baseOptions],
  );

  const ddlState = useDdlStatementDetection({ editorRef, query, options, enabled });
  const statementId = enabled && ddlState.visible ? ddlState.statement.id : null;

  const editContext = useInlineDdlEditContext({
    editorRef,
    tabId,
    // Disabled → park the service on an empty buffer with no connection so it
    // neither parses the live query nor reaches for a schema snapshot.
    query: enabled ? query : EMPTY_QUERY,
    statementId,
    options,
    connectionId: enabled ? connectionId : null,
    database: enabled ? database : null,
  });

  // The `'inline'`-sourced blast-radius gate is spliced on for EVERY object
  // kind — it is what guards drops from the non-table arms (T4: view /
  // sequence / trigger / routine / …) and the `drop` arm. On the `table` arm
  // it is intentionally superseded: `TableEditor` re-derives its own
  // `'designer'`-sourced gate from the same dependency snapshot and spreads it
  // over this context before handing it to the lifecycle/column affordances,
  // so the inline gate is dead code there by design (identical behaviour,
  // different `source` tag). Removing it here would leave the T4 arms
  // unguarded.
  const editContextWithGate = useInlineDdlBlastRadiusGate(editContext);

  const classification = enabled && ddlState.visible ? ddlState.classification : null;

  return useMemo<CompactDdlEditState>(
    () => ({
      editContext: editContextWithGate,
      objectKind: classification?.objectKind ?? null,
      ddlKind: classification?.ddlKind ?? null,
    }),
    [editContextWithGate, classification],
  );
}
